import { NestedTreeControl } from '@angular/cdk/tree';
import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  inject,
} from '@angular/core';
import { MatTreeNestedDataSource } from '@angular/material/tree';
import { TranslocoService } from '@jsverse/transloco';

import { saveAs } from 'file-saver-es';
import { capitalize } from 'humanize-plus';
import JSZip from 'jszip';
import { Subject, Subscription, takeUntil } from 'rxjs';
import { DropdownObject, TabObject } from '@inmoment/the-kitchen';

import { AppSharedModule } from 'app/app-shared.module';
import { AddTagsDialogComponent } from 'app/modules/collection/components/add-tags-dialog/add-tags-dialog.component';
import { ChooseTagsOptions } from 'app/modules/collection/components/remove-tags-dialog/remove-tags-dialog.component';
import { AuthService } from 'app/providers/bridge-api/auth.service';
import { CommentTarget, Widget } from 'app/providers/bridge-api/dashboard.service';
import { StorageAccountService } from 'app/providers/storage-api/blender-account.service';
import { DocumentService } from 'app/providers/storage-api/document.service';
import { Field } from 'app/providers/storage-api/field.service';
import {
  InteractiveSmartSummaryService,
  InteractiveSmartSummary,
  SmartSummary,
  SmartSummaryHierarchyNode,
  VerbatimExample,
  InteractiveSmartSummaryParams,
  SummaryFeedBackParameters,
  SmartSummaryFeedbackReport,
} from 'app/providers/storage-api/interactive-smart-summary.service';
import { AlertService } from 'app/services/alert.service';
import { LoggerService } from 'app/services/logger.service';
import {
  injectComponentTelemetryService,
  EnableComponentTelemetry,
  EnableClassTelemetry,
} from 'app/services/otel.service';
import { SystemService } from 'app/services/system.service';
import { TimezoneService } from 'app/services/timezone.service';
import { WidgetDataSource } from '../../../../models';
import { SmartSummaryFeedbackDialogComponent } from '../smart-summary-feedback-dialog/smart-summary-feedback-dialog.component';
import { MetadataField } from '../widget-add/widget-add.component';
import { SmartSummaryTextComponent } from './smart-summary-text/smart-summary-text.component';

export interface InteractiveSmartSummaryOptions {
  widget: Widget;
  widgetDataSource: WidgetDataSource;
  reportFilter: string;
  reportQuery: string;
  pointName: string;
  analyticItem: string;
  isReadOnly: boolean;
  isDebug: boolean;
  canAddComment: boolean;
  embeddingModel: string;
  embeddingStrategy: string;
  smartSummaryResult: InteractiveSmartSummary;
  overrideAnalysisId?: string;
  isDashboardLocked: boolean;
}

@EnableClassTelemetry()
@Component({
  selector: 'app-interactive-smart-summary',
  imports: [
    AppSharedModule,
    AddTagsDialogComponent,
    SmartSummaryFeedbackDialogComponent,
    SmartSummaryTextComponent,
  ],
  templateUrl: './interactive-smart-summary.component.html',
  styleUrls: ['./interactive-smart-summary.component.scss'],
})
export class InteractiveSmartSummaryComponent implements OnInit, OnDestroy {
  @EnableComponentTelemetry() otelService = injectComponentTelemetryService();

  @Input() options: InteractiveSmartSummaryOptions;
  @Output() modalClosed = new EventEmitter<void>();
  @Output() addComment = new EventEmitter<{
    smartSummaryId: string;
    title: string;
    details: string;
    target?: CommentTarget;
  }>();
  @Output() addNotes = new EventEmitter<string>();

  private translocoService = inject(TranslocoService);
  private interactiveSmartSummaryService = inject(InteractiveSmartSummaryService);
  private accountService = inject(StorageAccountService);
  private authService = inject(AuthService);
  private timezoneService = inject(TimezoneService);
  private systemService = inject(SystemService);
  private alertService = inject(AlertService);
  private documentService = inject(DocumentService);
  private cdr: ChangeDetectorRef = inject(ChangeDetectorRef);
  private logger = inject(LoggerService);

  public query = '';
  public projectId: string;
  public content: any;
  public isLoading: boolean;
  public isLoadingBreakdown: boolean;
  public isLoadingSummary: boolean;
  public isLoadingExample: boolean;
  public isFeatureSummaryItemDropdownButtonsImplemented: boolean = false;
  public breakdownToolTip: string;
  public isError: boolean;
  public navigationListItems = {};
  sideNavOpened: boolean;
  breakdownDropDownData: DropdownObject[] = [];
  similarity: number;
  similarityChanging: boolean;
  similarityToolTip: string;
  breakdownField: string;
  breakdownCategoryMap: {} = { likes: [], dislikes: [], improvements: [] };
  datasourceDisplayName = 'datasource';
  dataSourceDocumentCount = 23000;
  filteredDocumentCount = 8700;
  exampleSectionHeader = 'this is a summary';
  examplesLoading = false;
  addCommentTooltip: string;
  addNotesTooltip: string;
  addTagsTooltip: string;
  isSaving = false;

  private groupFieldsSub$: Subscription;
  dsMetadataFields: MetadataField[] = [];
  accountMetadataFields: Field[] = [];
  selectedSummary: SmartSummary;
  currentFeedbackSummary: SmartSummary;
  currentFeedback: SmartSummaryFeedbackReport;

  rightPanelTabs: TabObject[] = [{ title: '', id: 'examples' }];
  activeTab = 'examples';

  unsub$ = new Subject<void>();

  summaryDataSource = new MatTreeNestedDataSource<SmartSummaryHierarchyNode>();
  expandedNodes = new Set<SmartSummaryHierarchyNode>();
  isTopLevelNode = (_: number, node: SmartSummaryHierarchyNode) => !!node.isTopLevel;
  canCollapse: boolean;

  treeControl = new NestedTreeControl<SmartSummaryHierarchyNode>((node) => {
    const ret = [];
    if (node.children) {
      ret.push(...node.children);
    }
    return ret;
  });

  selectedNode: SmartSummaryHierarchyNode;

  currentExample: VerbatimExample;
  isCurrentExampleProcessing: boolean;
  showAddDocumentTagsModal: boolean;
  showSmartSummaryFeedbackModal: boolean;
  documentTagsModalOptions: ChooseTagsOptions;
  downloadEmbeddingReportTooltip: string;
  currentlyDownloadingEmbeddingReport: boolean;

  constructor() {}

  async ngOnInit() {
    this.isLoading = true;
    this.projectId = this.options.widget.payload.projectId;
    if (!this.options.canAddComment) {
      this.addCommentTooltip = this.translocoService.translate(
        'widgets.comments.not-enabled-for-widget'
      );
      if (this.options.isReadOnly) {
        this.addCommentTooltip = this.translocoService.translate('widgets.comments.not-enabled');
      }
    }
    if (this.options.isReadOnly) {
      this.addNotesTooltip = this.translocoService.translate(
        'widgets.interactive-smart-summary.add-as-note-disabled-readonly'
      );
      this.addTagsTooltip = this.translocoService.translate(
        'widgets.interactive-smart-summary.example-document-tag-disabled-readonly'
      );
    }
    this.similarity = this.options.smartSummaryResult.similarity ?? 0.75;
    this.similarityToolTip = this.translocoService.translate(
      'widgets.interactive-smart-summary.similarity-dropdown-tooltip'
    );
    this.downloadEmbeddingReportTooltip = this.translocoService.translate(
      'widgets.interactive-smart-summary.download-embedding-report'
    );
    this.groupFieldsSub$ = this.accountService.groupFieldsSub.subscribe((accountMetadata) => {
      this.accountMetadataFields = accountMetadata.metadata;
    });

    this.options.smartSummaryResult?.results?.forEach((r) => {
      for (const summary of r.summaries) {
        this.summarySetup(summary);
      }
      this.addDummySummaryIfNeeded(r);
    });
    this.breakdownCategoryMap = this.populateCategoryBreakdownMap(
      this.options.smartSummaryResult.results
    );
    this.isLoading = false;
    this.sideNavOpened = false;
    this.selectedNode = this.options.smartSummaryResult.results[0];
    this.selectTopSummaryOfSection(this.selectedNode);
    this.summaryDataSource.data = [...this.options.smartSummaryResult.results];

    this.translocoService
      .selectTranslateObject('widgets.interactive-smart-summary')
      .pipe(takeUntil(this.unsub$))
      .subscribe((t: { [key: string]: string }) => {
        this.rightPanelTabs[0].title = t['examples'];
      });

    this.findDatasourceFields(['double', 'long', 'keyword']);
    this.breakdownDropDownData = this.getKeywordFieldSet();

    //Recalculate numbers every load to deal with new documents.
    //Can update this later to limit to changed datasources if it becomes a problem.
    this.onSimilarityChange(this.similarity, false);
  }

  private populateCategoryBreakdownMap(nodes: SmartSummaryHierarchyNode[] | null): {} {
    if (!nodes) {
      return {};
    }
    const categoryBreakdownMap = {};
    function traverse(node: SmartSummaryHierarchyNode) {
      if (!node) {
        return;
      }
      if (node.isBreakdown && node.breakdownField) {
        categoryBreakdownMap[node.category] ??= [];
        const breakdowns = categoryBreakdownMap[node.category];
        if (breakdowns && !breakdowns.includes(node.breakdownField)) {
          breakdowns.push(node.breakdownField);
        }
      }
      if (Array.isArray(node.children)) {
        node.children.forEach(traverse);
      }
    }
    nodes.forEach(traverse);
    return categoryBreakdownMap;
  }

  private selectTopSummaryOfSection(section: SmartSummaryHierarchyNode) {
    this.selectedSummary = section.summaries.length > 0 ? section.summaries.at(0) : undefined;
  }

  private addDummySummaryIfNeeded(section: SmartSummaryHierarchyNode) {
    if (section.summaries.length > 0) {
      return;
    }
    const dummySummary = {
      summaryId: 'dummy',
      summary: this.translocoService.translate('widgets.interactive-smart-summary.no-summaries'),
      counts: {
        vectorCount: 0,
        totalCount: 0,
      },
      examples: [],
      examplesRequested: false,
      examplesExhausted: false,
      isDummy: true,
    };
    section.summaries.push(dummySummary);
  }

  public selectSummary(summary: SmartSummary) {
    this.selectedSummary = summary;
  }

  ngOnDestroy(): void {
    if (this.groupFieldsSub$) {
      this.groupFieldsSub$.unsubscribe();
    }
  }

  public onSummaryNodeClicked(node: SmartSummaryHierarchyNode, ensureExpanded = true) {
    if (!this.selectedNode || this.selectedNode !== node) {
      if (node && ensureExpanded) {
        const parentTree = this.getParentTreeForNode(node);
        for (const ancestor of parentTree) {
          if (!this.treeControl.isExpanded(ancestor)) {
            this.toggleNode(ancestor);
          }
        }
      }

      this.selectItem(node);
    }
  }

  public confirm(): void {
    this.modalClosed.emit();
  }

  collapseAll() {
    this.expandedNodes.clear();

    this.treeControl.collapseAll();

    this.canCollapse = false;

    if (!this.summaryDataSource.data.includes(this.selectedNode)) {
      this.selectItem(this.summaryDataSource.data[0]);
    }
  }

  selectItem(item: SmartSummaryHierarchyNode) {
    if (!item) {
      return;
    }
    this.selectedNode = undefined;
    if (item.isBreakdown && item.breakdownField) {
      this.breakdownField = item.breakdownField;
    } else {
      this.breakdownField = undefined;
    }

    if (item) {
      this.selectedNode = item;
      const parent = this.getDirectParentForNode(this.selectedNode);
      if (parent) {
        this.treeControl.expand(parent);
        this.expandedNodes.add(parent);
      }
    }
    this.selectTopSummaryOfSection(item);
  }

  private getParentTree(arr: any[], item, childProp?) {
    if (arr) {
      for (const t of arr) {
        if (t === item || (childProp && t[childProp] && t[childProp].includes(item))) {
          return [t];
        }

        const ret = this.getParentTree(t.children, item, childProp);
        if (ret) {
          return [t, ...ret];
        }
      }
    }

    return undefined;
  }

  getParentTreeForNode(node: SmartSummaryHierarchyNode): SmartSummaryHierarchyNode[] {
    return this.getParentTree(this.summaryDataSource.data, node);
  }

  getDirectParentForNode(node: SmartSummaryHierarchyNode) {
    const parentTree = this.getParentTreeForNode(node);
    if (parentTree.length > 1) {
      return parentTree[parentTree.length - 2];
    }
    return parentTree[parentTree.length - 1];
  }

  getRootParentForNode(node: SmartSummaryHierarchyNode) {
    const parentTree = this.getParentTreeForNode(node);
    return parentTree[0];
  }

  async toggleAndSelectNode($event, node) {
    $event?.stopImmediatePropagation();
    this.onSummaryNodeClicked(node, false);
    this.toggleNode(node);
  }

  private toggleNode(node) {
    this.treeControl.toggle(node);

    if (this.treeControl.isExpanded(node)) {
      this.expandedNodes.add(node);
    } else {
      this.expandedNodes.delete(node);
    }

    this.canCollapse = !!this.expandedNodes.size;
  }

  get isValid() {
    return !this.selectedNode;
  }

  async updateSummary() {
    if (this.isSaving) {
      return;
    }
    this.isSaving = true;
    this.options.smartSummaryResult.results = this.summaryDataSource.data;
    await this.interactiveSmartSummaryService.saveSmartSummary(
      this.projectId,
      this.options.smartSummaryResult
    );
    this.cdr.detectChanges();
    this.isSaving = false;
  }

  private async getSummaryFromSpotlight(summary: SmartSummary) {
    const existingSummaries = this.getExistingSummaries(summary);
    let summaryResult;
    try {
      const summaryRequest: InteractiveSmartSummaryParams = {
        paragraphLength: 1,
        analyticItemName: this.options.pointName,
        analyticItemType: this.options.analyticItem || 'metadata',
        existingSummaries,
        category: this.selectedNode.category,
        breakdownField: summary?.breakdownField,
        breakdownValue: summary?.breakdownValue,
      };
      summaryResult = await this.interactiveSmartSummaryService.regenerateSmartSummary(
        this.options.widgetDataSource.collectionName,
        this.options.widgetDataSource.groupId,
        this.options.overrideAnalysisId ?? this.options.widgetDataSource.analysisId,
        summaryRequest,
        this.timezoneService.timezone,
        this.options.reportQuery,
        this.projectId
      );
    } catch (err) {
      summaryResult = {
        summary: this.translocoService.translate(
          'widgets.interactive-smart-summary.error-generating-summary'
        ),
        counts: {
          vectorCount: 0,
          totalCount: 0,
        },
        examples: [],
        examplesRequested: false,
        examplesExhausted: false,
        isErrored: true,
      };
    }
    return summaryResult;
  }

  async regenerateSummary(summary: SmartSummary) {
    this.isLoadingSummary = true;
    const index = this.selectedNode.summaries.indexOf(summary, 0);
    if (index > -1) {
      summary.regenerating = true;
      this.selectedNode.summaries[index] = summary;
    }
    const selectedBeforeGeneration = this.selectedNode;
    if (!this.selectedNode.rejectedSummaries) {
      this.selectedNode.rejectedSummaries = [];
    }
    this.selectedNode.rejectedSummaries.push(summary);
    const summaryResult = await this.getSummaryFromSpotlight(summary);
    this.summarySetup(summaryResult);
    selectedBeforeGeneration.summaries[index] = summaryResult;
    if (this.selectedSummary && this.selectedSummary.summary === summary.summary) {
      this.selectedSummary = summaryResult;
    }
    if (selectedBeforeGeneration.isBreakdown) {
      this.checkRemovableBreakdownSummaries(selectedBeforeGeneration.summaries);
    }
    this.isLoadingSummary = false;
    await this.updateSummary();
  }

  async generateSummary(summary?: SmartSummary) {
    this.isLoadingSummary = true;
    const dummySummary = {
      summaryId: 'dummy',
      summary: this.translocoService.translate('widgets.interactive-smart-summary.generating'),
      counts: {
        vectorCount: 0,
        totalCount: 0,
      },
      examples: [],
      examplesRequested: false,
      examplesExhausted: false,
    };
    let index = 0;
    //For breakdown we need to use the breakdown value of a given bucket.
    //For now the only way to generate a summary from a breakdown is via another summary for now.
    //TODO:  Ask for desired UI to select the buckets.
    if (!summary) {
      this.selectedNode.summaries.unshift(dummySummary);
    } else {
      index = this.selectedNode.summaries.indexOf(summary, 0);
      if (index > -1) {
        this.selectedNode.summaries.splice(index, 0, dummySummary);
      } else {
        index = 0;
        this.selectedNode.summaries.unshift(dummySummary);
      }
    }
    this.selectedSummary = dummySummary;
    const selectedBeforeGeneration = this.selectedNode;
    const summaryResult = await this.getSummaryFromSpotlight(summary);
    this.summarySetup(summaryResult);
    selectedBeforeGeneration.summaries[index] = summaryResult;
    if (this.selectedSummary && this.selectedSummary.summary === summary?.summary) {
      this.selectedSummary = summaryResult;
    }
    if (selectedBeforeGeneration.isBreakdown) {
      this.checkRemovableBreakdownSummaries(selectedBeforeGeneration.summaries);
    }
    this.isLoadingSummary = false;
    await this.updateSummary();
  }

  async removeSummary(summary: SmartSummary) {
    const index = this.selectedNode.summaries.indexOf(summary, 0);
    if (index > -1) {
      if (!this.selectedNode.rejectedSummaries) {
        this.selectedNode.rejectedSummaries = [];
      }
      this.selectedNode.rejectedSummaries.push(summary);
      this.selectedNode.summaries.splice(index, 1);
      if (this.selectedSummary === summary) {
        this.selectTopSummaryOfSection(this.selectedNode);
      }
      if (this.selectedNode.isBreakdown) {
        this.checkRemovableBreakdownSummaries(this.selectedNode.summaries);
      }
    }
    await this.updateSummary();
  }

  async removeExample(example: VerbatimExample) {
    if (!this.selectedSummary.examples) {
      return;
    }
    const index = this.selectedSummary.examples.indexOf(example, 0);
    if (index > -1) {
      if (this.selectedSummary.ignoredExamples) {
        this.selectedSummary.ignoredExamples.push(example);
      } else {
        this.selectedSummary.ignoredExamples = [example];
      }
      this.selectedSummary.examples.splice(index, 1);
    }
    await this.updateSummary();
  }

  async leaveSummaryFeedback(summary: SmartSummary) {
    if (summary?.feedbackId) {
      this.currentFeedback = await this.interactiveSmartSummaryService.getSmartSummaryFeedback(
        this.projectId,
        summary.feedbackId
      );
    }
    this.showSmartSummaryFeedbackModal = true;
    this.currentFeedbackSummary = summary;
  }

  async removeSummaryFeedback(summary: SmartSummary) {
    await this.interactiveSmartSummaryService.deleteSmartSummaryFeedback(
      this.projectId,
      summary.feedbackId
    );
    summary.feedbackId = undefined;
    this.alertService.showToast(
      this.translocoService.translate('widgets.interactive-smart-summary.feedback-removed'),
      2000
    );
    this.cdr.detectChanges();
  }

  async onSummaryFeedbackClosed($event) {
    this.showSmartSummaryFeedbackModal = false;

    if (!$event) {
      this.currentFeedbackSummary = undefined;
      this.currentFeedback = undefined;
      return;
    }
    this.currentFeedbackSummary.summaryFeedbackTooltip = this.translocoService.translate(
      'widgets.interactive-smart-summary.currently-generating-feedback'
    );
    this.currentFeedbackSummary.currentlyGeneratingFeedback = true;
    const existingSummaries = this.getExistingSummaries(this.currentFeedbackSummary);
    try {
      const analyticItemType = this.options.analyticItem || 'metadata';
      const breakdownField = this.currentFeedbackSummary?.breakdownField;
      const breakdownValue = this.currentFeedbackSummary?.breakdownValue;
      this.alertService.showToast(
        this.translocoService.translate('widgets.interactive-smart-summary.generating-feedback'),
        2000
      );
      const feedbackRequestSummaryParams: InteractiveSmartSummaryParams = {
        paragraphLength: 1,
        analyticItemName: this.options.pointName,
        analyticItemType,
        existingSummaries,
        category: this.selectedNode.category,
        breakdownField,
        breakdownValue,
        elasticRandomSeed: this.currentFeedbackSummary.elasticRandomSeed,
        elasticQuery: this.options.smartSummaryResult.payload.query,
      };
      const feedbackParams: SummaryFeedBackParameters = {
        summary: this.currentFeedbackSummary,
        smartSummaryParameters: feedbackRequestSummaryParams,
        userFeedback: $event.data,
        feedbackType: $event.type,
        collectionId: this.options.widgetDataSource.collectionName,
        projectId: this.projectId,
        widgetId: this.options.widget.id,
        dashboardId: this.options.smartSummaryResult.dashboardId,
        dataSourceId: this.options.smartSummaryResult.dashboardId,
        feedbackId: this.currentFeedbackSummary.feedbackId,
      };
      const spotERequest = await this.interactiveSmartSummaryService.generateFeedBackForSummary(
        this.options.widgetDataSource.collectionName,
        this.options.widgetDataSource.groupId,
        this.options.overrideAnalysisId ?? this.options.widgetDataSource.analysisId,
        feedbackParams,
        this.timezoneService.timezone,
        this.options.reportQuery,
        this.projectId
      );
      this.currentFeedbackSummary.feedbackId = spotERequest.id;
      this.alertService.showToast(
        this.translocoService.translate('widgets.interactive-smart-summary.feedback-generated'),
        2000
      );
    } finally {
      this.currentFeedbackSummary.summaryFeedbackTooltip = '';
      this.currentFeedbackSummary.currentlyGeneratingFeedback = false;
      this.currentFeedbackSummary = undefined;
      this.currentFeedback = undefined;
    }
  }

  copyToClipBoard(summary: SmartSummary) {
    this.systemService.copyToClipboard(summary.summary);
    this.alertService.showToast(
      this.translocoService.translate('widgets.interactive-smart-summary.copied-to-clipboard')
    );
  }

  addAsComment(summary: SmartSummary) {
    this.addComment.emit({
      smartSummaryId: this.options.smartSummaryResult.id,
      title: summary.header,
      details: summary.summary,
      target: this.options.widget.payload.comments?.find(
        (c) => c.smartSummaryId === this.options.smartSummaryResult.id
      )?.target,
    });
  }

  addAsNoteWidget(summary: SmartSummary) {
    this.addNotes.emit(summary.summary);
  }

  private getExistingSummaries(summary: SmartSummary) {
    const summaryBreakdown = summary?.breakdownValue ?? undefined;
    const existingSummaries: string[] = [];
    const addToExisting = (sum) => {
      const sumBreakdown = sum?.breakdownValue != null ? sum.breakdownValue : undefined;
      const shouldAddSummary =
        sum.summary !== 'Generating...' &&
        ((sumBreakdown === undefined && summaryBreakdown === undefined) ||
          (sumBreakdown !== undefined && sumBreakdown === summaryBreakdown));
      if (shouldAddSummary) {
        existingSummaries.push(sum.summary);
      }
    };
    if (this.selectedNode.rejectedSummaries) {
      this.selectedNode.rejectedSummaries.forEach((sum) => {
        addToExisting(sum);
      });
    }
    if (this.selectedNode.summaries) {
      this.selectedNode.summaries.forEach((sum) => {
        addToExisting(sum);
      });
    }
    return existingSummaries;
  }

  async downloadSpotERequest(summary: SmartSummary) {
    summary.downloadRequestTooltip = this.translocoService.translate(
      'widgets.interactive-smart-summary.currently-downloading-spote-request'
    );
    summary.currentlyDownloadingSpotERequest = true;
    const existingSummaries = this.getExistingSummaries(summary);
    try {
      const analyticItemType = this.options.analyticItem ? this.options.analyticItem : 'metadata';
      const breakdownField = summary ? summary.breakdownField : undefined;
      const breakdownValue = summary ? summary.breakdownValue : undefined;
      const spotERequest = await this.interactiveSmartSummaryService.getSpotEReqestForSummary(
        this.options.widgetDataSource.collectionName,
        this.options.widgetDataSource.groupId,
        this.options.overrideAnalysisId ?? this.options.widgetDataSource.analysisId,
        {
          paragraphLength: 1,
          analyticItemName: this.options.pointName,
          analyticItemType,
          existingSummaries,
          category: this.selectedNode.category,
          breakdownField,
          breakdownValue,
        },
        this.timezoneService.timezone,
        this.options.reportQuery,
        this.projectId
      );
      const jsonString = JSON.stringify(spotERequest);
      let filename;
      if (breakdownField) {
        filename = `${analyticItemType}_${this.options.pointName}_${breakdownField}_${breakdownValue}_spote_request.json`;
      } else {
        filename = `${analyticItemType}_${this.options.pointName}_spote_request.json`;
      }
      const blob = new Blob([jsonString], { type: 'application/json' });
      saveAs(blob, filename);
    } finally {
      summary.downloadRequestTooltip = '';
      summary.currentlyDownloadingSpotERequest = false;
    }
  }

  downloadZipWithJsonFiles(jsonFiles: { fileName: string; content: any }[], outputBase: string) {
    const zip = new JSZip();

    // Loop through each JSON file and add it to the ZIP
    jsonFiles.forEach((file) => {
      // Convert the JSON content to a string
      const jsonString = JSON.stringify(file.content, null, 2);

      // Add the file to the ZIP
      zip.file(file.fileName, jsonString);
    });

    // Generate the ZIP file and trigger download
    zip
      .generateAsync({ type: 'blob' })
      .then((content) => {
        // Trigger the download using FileSaver.js
        saveAs(content, `${outputBase}.zip`);
      })
      .catch((error) => {
        this.logger.error('Error generating ZIP file:', error);
      });
  }

  async getAllEmbeddingDocs() {
    this.downloadEmbeddingReportTooltip = this.translocoService.translate(
      'widgets.interactive-smart-summary.currently-downloading-embedding-report'
    );
    this.currentlyDownloadingEmbeddingReport = true;
    try {
      const analyticItemType = this.options.analyticItem ? this.options.analyticItem : 'metadata';
      const spotEResponse = await this.interactiveSmartSummaryService.dumpEmbeddings(
        this.options.widgetDataSource.collectionName,
        this.options.widgetDataSource.groupId,
        this.options.overrideAnalysisId ?? this.options.widgetDataSource.analysisId,
        this.selectedNode.summaries[0],
        this.options.reportQuery,
        this.projectId,
        '0.0',
        this.timezoneService.timezone
      );
      const response = {};
      const summaries = {
        likes: [],
        dislikes: [],
        improvements: [],
      };
      response['summaries'] = summaries;
      this.summaryDataSource.data.forEach((summaryNode) => {
        if (summaryNode.category === 'likes') {
          summaryNode.summaries.forEach((summary) => {
            response['summaries']['likes'].push(summary.summary);
          });
        }
        if (summaryNode.category === 'dislikes') {
          summaryNode.summaries.forEach((summary) => {
            response['summaries']['dislikes'].push(summary.summary);
          });
        }
        if (summaryNode.category === 'improvements') {
          summaryNode.summaries.forEach((summary) => {
            response['summaries']['improvements'].push(summary.summary);
          });
        }
      });
      response['documents'] = spotEResponse;
      const filename = `all_docs_${analyticItemType}_${this.options.pointName}.json`;
      const final_response = {};
      final_response['fileName'] = filename;
      final_response['content'] = response;
      return final_response;
    } catch (e) {}
  }

  async downloadEmbeddingDocs() {
    this.downloadEmbeddingReportTooltip = this.translocoService.translate(
      'widgets.interactive-smart-summary.currently-downloading-embedding-report'
    );
    this.currentlyDownloadingEmbeddingReport = true;
    const analyticItemType = this.options.analyticItem ? this.options.analyticItem : 'metadata';

    try {
      const jsonFiles = [];
      const requests = [];

      // Gather all requests in an array of promises
      for (const summaryNode of this.summaryDataSource.data) {
        for (const summary of summaryNode.summaries) {
          const request = this.interactiveSmartSummaryService
            .dumpEmbeddings(
              this.options.widgetDataSource.collectionName,
              this.options.widgetDataSource.groupId,
              this.options.overrideAnalysisId ?? this.options.widgetDataSource.analysisId,
              summary,
              this.options.reportQuery,
              this.projectId,
              this.similarity as unknown as string,
              this.timezoneService.timezone
            )
            .then((spotEResponse) => {
              const response = {
                summary: summary.summary,
                documents: spotEResponse,
              };

              const index = summaryNode.summaries.indexOf(summary);
              const filename = `embedded_docs_${summaryNode.category}_${index + 1}_edocs.json`;

              jsonFiles.push({
                fileName: filename,
                content: response,
              });
            });

          // Add the request to the list of promises
          requests.push(request);
        }
      }
      const allRequest = this.getAllEmbeddingDocs().then((response) => {
        jsonFiles.push(response);
      });
      requests.push(allRequest);
      // Wait for all requests to complete
      await Promise.all(requests);

      // Generate the ZIP file and trigger download
      const outputBase = `${(this.similarity * 100).toFixed(0)}_${analyticItemType}_${
        this.options.pointName
      }_edocs`;
      this.downloadZipWithJsonFiles(jsonFiles, outputBase);
    } finally {
      this.downloadEmbeddingReportTooltip = this.translocoService.translate(
        'widgets.interactive-smart-summary.download-embedding-report'
      );
      this.currentlyDownloadingEmbeddingReport = false;
    }
  }

  async downloadSummaryEmbeddingDocs(summary: SmartSummary) {
    summary.downloadEmbeddingReportTooltip = this.translocoService.translate(
      'widgets.interactive-smart-summary.currently-downloading-embedding-report'
    );
    summary.currentlyDownloadingEmbeddingReport = true;
    try {
      const analyticItemType = this.options.analyticItem ? this.options.analyticItem : 'metadata';
      const breakdownField = summary ? summary.breakdownField : undefined;
      const breakdownValue = summary ? summary.breakdownValue : undefined;
      const spotEResponse = await this.interactiveSmartSummaryService.dumpEmbeddings(
        this.options.widgetDataSource.collectionName,
        this.options.widgetDataSource.groupId,
        this.options.overrideAnalysisId ?? this.options.widgetDataSource.analysisId,
        summary,
        this.options.reportQuery,
        this.projectId,
        this.similarity as unknown as string,
        this.timezoneService.timezone
      );
      const response = {};
      response['summary'] = summary.summary;
      response['documents'] = spotEResponse;
      const jsonString = JSON.stringify(response);
      const index = this.selectedNode.summaries.indexOf(summary, 0);
      let filename;
      const firstXWidgetId = this.options.widget.id.slice(0, 6);
      if (breakdownField) {
        filename = `${firstXWidgetId}_${this.selectedNode.category}_summary-${
          index + 1
        }_similarity-${(this.similarity * 100).toFixed(0)}_${analyticItemType}_${
          this.options.pointName
        }_${breakdownField}_${breakdownValue}_edocs.json`;
      } else {
        filename = `${firstXWidgetId}_${this.selectedNode.category}_summary-${
          index + 1
        }_similarity-${(this.similarity * 100).toFixed(0)}_${analyticItemType}_${
          this.options.pointName
        }_edocs.json`;
      }
      const blob = new Blob([jsonString], { type: 'application/json' });
      saveAs(blob, filename);
    } finally {
      summary.downloadEmbeddingReportTooltip = this.translocoService.translate(
        'widgets.interactive-smart-summary.download-embedding-report'
      );
      summary.currentlyDownloadingEmbeddingReport = false;
    }
  }

  async getAnotherExample() {
    this.isLoadingExample = true;
    const dummyExample = {
      text: '',
      id: '',
      publicId: '',
      fetchingAnother: true,
    };
    if (!this.selectedSummary.examples) {
      this.selectedSummary.examples = [];
    }
    this.selectedSummary.examples.unshift(dummyExample);
    const examples = await this.interactiveSmartSummaryService.getSmartSummaryExamples(
      this.options.widgetDataSource.collectionName,
      this.options.widgetDataSource.groupId,
      this.options.overrideAnalysisId ?? this.options.widgetDataSource.analysisId,
      this.selectedSummary,
      this.options.reportQuery,
      this.projectId,
      this.similarity as unknown as string,
      this.timezoneService.timezone
    );
    const exampleResult =
      examples.length > 0
        ? examples[0]
        : {
            text: this.translocoService.translate(
              'widgets.interactive-smart-summary.no-verbatim-found'
            ),
            id: 'no_more_examples',
            publicId: 'no_more_examples',
          };
    this.selectedSummary.examples[0] = exampleResult;
    this.checkNoMoreExamples();
    if (examples.length === 0) {
      this.selectedSummary.examplesExhausted = true;
    }
    this.isLoadingExample = false;
    await this.updateSummary();
  }

  copyExampleToClipboard(example: VerbatimExample) {
    this.systemService.copyToClipboard(example.text);
    this.alertService.showToast(
      this.translocoService.translate(
        'widgets.interactive-smart-summary.example-copied-to-clipboard'
      )
    );
  }

  isProcessingExample(example: VerbatimExample) {
    return this.isCurrentExampleProcessing && example.id === this.currentExample?.id;
  }

  tagExampleDocument(example: VerbatimExample) {
    this.currentExample = example;
    this.documentTagsModalOptions = {
      content: this.translocoService.translate(
        'widgets.interactive-smart-summary.tag-this-document'
      ),
      placeholder: this.translocoService.translate('widgets.interactive-smart-summary.new-tag'),
    };
    this.showAddDocumentTagsModal = true;
  }

  async onDocumentTagClosed($event) {
    this.showAddDocumentTagsModal = false;

    if (!$event) {
      return;
    }

    try {
      this.isCurrentExampleProcessing = true;

      await this.documentService.tagDocumentsByDocIds(
        this.options.widgetDataSource.collectionName,
        [this.currentExample.publicId],
        $event,
        false,
        this.projectId
      );
      this.alertService.showToast(
        this.translocoService.translate('widgets.interactive-smart-summary.example-document-tagged')
      );
    } catch (err) {
      this.alertService.showToast(
        this.translocoService.translate(
          'widgets.interactive-smart-summary.example-document-tag-failed'
        )
      );
      this.logger.error('Error tagging example document:', { error: err });
    } finally {
      this.isCurrentExampleProcessing = false;
    }
  }

  async generateExamples(summary: SmartSummary) {
    this.selectedSummary = summary;
    if (summary.examples && summary.examplesRequested) {
      return;
    }
    this.examplesLoading = true;
    try {
      summary.examples = await this.interactiveSmartSummaryService.getSmartSummaryExamples(
        this.options.widgetDataSource.collectionName,
        this.options.widgetDataSource.groupId,
        this.options.overrideAnalysisId ?? this.options.widgetDataSource.analysisId,
        summary,
        this.options.reportQuery,
        this.projectId,
        this.similarity as unknown as string,
        this.timezoneService.timezone
      );
      summary.exampleSectionHeader = summary.summary.substring(0, 30) + '...';
      this.checkNoMoreExamples();
      summary.examplesRequested = true;
      this.examplesLoading = false;
      await this.updateSummary();
    } finally {
      summary.examplesRequested = true;
      this.examplesLoading = false;
    }
  }

  private findDatasourceFields(fieldType?: string | string[]) {
    this.dsMetadataFields = this.accountMetadataFields
      .filter((f) => this.options.widgetDataSource?.customFields?.includes(f.fieldName))
      .map((f) => ({
        name: f.fieldName,
        label: f.description,
        type: f.fieldType,
        isRating: f.metadata?.rating === true,
        isRedacted: f.metadata?.redacted === true,
        isDisplayed: f.metadata?.display !== false,
      }));

    if (fieldType) {
      this.dsMetadataFields = this.dsMetadataFields.filter((f) => {
        if (typeof fieldType === 'string') {
          if (fieldType === 'rating') {
            return f.isRating;
          }
          return f.type === fieldType;
        }

        if (fieldType.includes('rating')) {
          return f.isRating || fieldType.includes(f.type);
        }
        return fieldType.includes(f.type);
      });
    }

    this.dsMetadataFields.sort((f1, f2) =>
      f1.label.localeCompare(f2.label, undefined, { sensitivity: 'accent' })
    );
  }

  checkNoMoreExamples() {
    if (!this.selectedSummary) {
      return;
    }
    const exampleCount = this.selectedSummary.examples ? this.selectedSummary.examples.length : 0;
    const ignoredExamples = this.selectedSummary.ignoredExamples
      ? this.selectedSummary.ignoredExamples.length
      : 0;
    this.selectedSummary.examplesExhausted =
      this.selectedSummary.counts.vectorCount <= exampleCount + ignoredExamples;
    if (this.selectedSummary.examplesExhausted) {
      this.selectedSummary.anotherExampleTooltip = this.translocoService.translate(
        'widgets.interactive-smart-summary.example-verbatims-exhausted'
      );
    }
  }

  private getKeywordFieldSet(): DropdownObject[] {
    const ALLOWED_STANDARD_FIELDS = [
      'tags',
      'creator',
      'language',
      'publisher',
      'source',
      'type',
      'url',
    ];

    const standardFields = this.options.widgetDataSource.standardFields
      .filter((f) => ALLOWED_STANDARD_FIELDS.includes(f))
      .map((f) => ({ title: capitalize(f), id: f }));

    return this.dsMetadataFields
      .filter(
        (f) =>
          f.isDisplayed &&
          f.type === 'keyword' &&
          (!f.isRedacted || !this.authService.isAlwaysRedact)
      )
      .map((f) => ({ title: f.label, id: f.name }))
      .concat(standardFields)
      .sort((a, b) => {
        return a.title.toLocaleLowerCase().localeCompare(b.title.toLocaleLowerCase());
      });
  }

  private checkRemovableBreakdownSummaries(summaries: SmartSummary[]): void {
    const valueCount: { [key: string]: number } = {};
    summaries.forEach((summary) => {
      const key = summary.breakdownValue !== undefined ? summary.breakdownValue : 'undefined';
      if (valueCount[key] !== undefined) {
        valueCount[key]++;
      } else {
        valueCount[key] = 1;
      }
    });
    summaries.forEach((summary) => {
      const key = summary.breakdownValue !== undefined ? summary.breakdownValue : 'undefined';
      summary.disableRemove = valueCount[key] === 1;
      if (summary.disableRemove) {
        summary.disableRemoveTooltip = this.translocoService.translate(
          'widgets.interactive-smart-summary.remove-disabled'
        );
      }
    });
  }

  private async breakDownSmartSummary(
    breakdownDisplay: string,
    breakdownField: string,
    parentNode: SmartSummaryHierarchyNode
  ) {
    let summaryResult;
    try {
      summaryResult = await this.interactiveSmartSummaryService.breakdownSmartSummary(
        this.options.widgetDataSource.collectionName,
        this.options.widgetDataSource.groupId,
        this.options.overrideAnalysisId ?? this.options.widgetDataSource.analysisId,
        {
          paragraphLength: 1,
          analyticItemName: this.options.pointName,
          analyticItemType: this.options.analyticItem ? this.options.analyticItem : 'metadata',
          existingSummaries: [],
          category: this.selectedNode.category,
        },
        breakdownField,
        this.timezoneService.timezone,
        this.options.reportQuery,
        this.projectId
      );
    } catch (err) {
      summaryResult = {
        summaries: [
          {
            summary: this.translocoService.translate(
              'widgets.interactive-smart-summary.error-generating-summary'
            ),
            counts: {
              vectorCount: 0,
              totalCount: 0,
            },
            examples: [],
            examplesRequested: false,
            examplesExhausted: false,
            isErrored: true,
          },
        ],
      };
    }

    const breakdownNode = {
      name: this.translocoService.translate('widgets.interactive-smart-summary.breakdown-by', {
        breakdown: breakdownDisplay,
      }),
      summaries: summaryResult.summaries,
      children: [],
      isTopLevel: false,
      category: this.selectedNode.category,
      isBreakdown: true,
      breakdownField,
      rejectedSummaries: [],
    };
    for (const summary of breakdownNode.summaries) {
      this.summarySetup(summary);
    }
    this.addDummySummaryIfNeeded(breakdownNode);
    if (!parentNode.children) {
      parentNode.children = [];
    }
    parentNode.children.push(breakdownNode);
    if (!this.treeControl.isExpanded(parentNode)) {
      this.toggleNode(parentNode);
    }
    this.selectedNode = breakdownNode;

    this.checkRemovableBreakdownSummaries(breakdownNode.summaries);

    //Trigger refresh
    const data = this.summaryDataSource.data;
    // data needs to be iterable in order for us to not get an error, spread is not sufficient since child elements
    this.summaryDataSource.data = [];
    this.summaryDataSource.data = data;
    this.toggleAndSelectNode(undefined, this.selectedNode);
    this.selectTopSummaryOfSection(breakdownNode);
    await this.updateSummary();
  }

  private summarySetup(summary: SmartSummary) {
    if (summary.counts.countError) {
      summary.countToolTip = this.translocoService.translate(
        'widgets.interactive-smart-summary.count-error-tooltip'
      );
    } else {
      summary.countToolTip = this.translocoService.translate(
        'widgets.interactive-smart-summary.count-tooltip',
        {
          vectorCount: summary.counts.vectorCount,
          totalCount: summary.counts.totalCount,
        }
      );
    }
    summary.anotherExampleTooltip = this.translocoService.translate(
      'widgets.interactive-smart-summary.example-verbatims-another-tooltip'
    );
    summary.downloadEmbeddingReportTooltip = this.translocoService.translate(
      'widgets.interactive-smart-summary.download-embedding-report'
    );
    summary.accuracyReportTooltip = this.translocoService.translate(
      'widgets.interactive-smart-summary.download-accuracy-report'
    );
    summary.percentage = Math.floor((summary.counts.vectorCount / summary.counts.totalCount) * 100);
    summary.examplesRequested = summary.examples && summary.examples.length > 0;
  }

  async onBreakdownBreakdownFieldChange(event: any) {
    this.breakdownField = event.detail.selected.id;
    const breakdownCategory = this.selectedNode.category;
    //TODO: this will change when we have drilldown done.
    if (
      !this.selectedNode.isTopLevel ||
      this.breakdownCategoryMap[this.selectedNode.category]?.includes(this.breakdownField)
    ) {
      this.selectedNode.children ??= [];
      const breakdownNode = this.selectedNode.children.find(
        (node) => node.breakdownField === this.breakdownField
      );
      this.selectItem(breakdownNode);
      return;
    }
    this.isLoadingBreakdown = true;
    this.breakdownToolTip = this.translocoService.translate(
      'widgets.interactive-smart-summary.breakdown-loading'
    );
    await this.breakDownSmartSummary(
      event.detail.selected.title,
      this.breakdownField,
      this.selectedNode
    );
    this.breakdownCategoryMap[breakdownCategory] ??= [];
    this.breakdownCategoryMap[breakdownCategory].push(this.breakdownField);
    this.isLoadingBreakdown = false;
    this.breakdownToolTip = '';
  }

  async onSimilarityChange(similarity, clearExamples) {
    if (this.similarityChanging) {
      return;
    }
    this.similarityChanging = true;
    this.similarityToolTip = this.translocoService.translate(
      'widgets.interactive-smart-summary.similarity-dropdown-tooltip-loading'
    );
    this.similarity = similarity;

    const processNode = async (node) => {
      const summaryPromises = node.summaries.map(async (summary) => {
        if (!summary.isDummy) {
          summary.counts = await this.interactiveSmartSummaryService.getSmartSummaryCounts(
            this.options.widgetDataSource.collectionName,
            this.options.widgetDataSource.groupId,
            this.options.overrideAnalysisId ?? this.options.widgetDataSource.analysisId,
            summary,
            this.options.reportQuery,
            this.projectId,
            this.similarity as unknown as string,
            this.timezoneService.timezone
          );
          if (clearExamples) {
            summary.examplesRequested = false;
            summary.examples = undefined;
          }
          this.summarySetup(summary);
        }
      });

      await Promise.all(summaryPromises);

      if (node.children && node.children.length > 0) {
        const childPromises = node.children.map(processNode);
        await Promise.all(childPromises);
      }
    };

    try {
      const nodePromises = this.summaryDataSource.data.map(processNode);
      await Promise.all(nodePromises);
    } finally {
      this.similarityChanging = false;
      this.similarityToolTip = this.translocoService.translate(
        'widgets.interactive-smart-summary.similarity-dropdown-tooltip'
      );
    }
  }

  async analyzeSummary(summary: SmartSummary) {
    const index = this.selectedNode.summaries.indexOf(summary);
    this.alertService.showToast(
      this.translocoService.translate('widgets.interactive-smart-summary.analyzing-summary'),
      2000
    );
    try {
      const analysisResult = await this.interactiveSmartSummaryService.analyzeSmartSummary(
        this.options.widgetDataSource.collectionName,
        this.options.widgetDataSource.groupId,
        this.options.overrideAnalysisId ?? this.options.widgetDataSource.analysisId,
        summary,
        this.timezoneService.timezone,
        this.projectId
      );
      this.selectedNode.summaries[index] = analysisResult;
      await this.updateSummary();
      this.alertService.showToast(
        this.translocoService.translate(
          'widgets.interactive-smart-summary.analyzing-summary-complete'
        ),
        2000
      );
    } catch (err) {
      this.alertService.showErrorToast(
        this.translocoService.translate(
          'widgets.interactive-smart-summary.error-analyzing-summary'
        ),
        err
      );
    }
  }
}
